import React from 'react';
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import App from './App.tsx'
import './index.css'
import { AuthProvider } from "@/hooks/useAuth";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { initI18n } from "@/i18n";
import { Capacitor } from "@capacitor/core";

/**
 * Platform Detection and Body Class Setup
 * Adds .platform-android, .platform-ios, or .platform-web class to body
 * This enables platform-specific CSS targeting
 */
function setupPlatformClass() {
  const userAgent = navigator.userAgent.toLowerCase();
  
  // Check for Capacitor native app first
  const isCapacitor = 'Capacitor' in window;
  
  // Detect platform
  let platform = 'web';
  if (isCapacitor) {
    // In Capacitor, check for Android or iOS
    if (userAgent.includes('android')) {
      platform = 'android';
    } else if (userAgent.includes('iphone') || userAgent.includes('ipad') || userAgent.includes('ipod')) {
      platform = 'ios';
    }
  } else {
    // Web browser detection
    if (userAgent.includes('android')) {
      platform = 'android';
    } else if (userAgent.includes('iphone') || userAgent.includes('ipad') || userAgent.includes('ipod')) {
      platform = 'ios';
    }
  }
  
  // Add platform class to body
  document.body.classList.add(`platform-${platform}`);
  
  // Also set as data attribute for CSS selectors
  document.documentElement.setAttribute('data-platform', platform);
  
  console.log(`[STRAIV] Platform detected: ${platform}`);
}

// Initialize platform detection immediately
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupPlatformClass);
  } else {
    setupPlatformClass();
  }
}

// Initialize Google Auth asynchronously to prevent app crash
(async () => {
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');
    const platform = Capacitor.getPlatform();
    
    // Get client ID based on platform
    // IMPORTANT: For Android, use the WEB Client ID, not the Android Client ID
    // The Android Client ID is only for app identification (google-services.json)
    // The WEB Client ID is used for the actual sign-in flow
    let clientId: string;
    
    if (platform === 'ios') {
      clientId = '5370505998-758l7b9hr488v29cefv5t71uied9gvv7.apps.googleusercontent.com';
    } else if (platform === 'android') {
      // Android MUST use the WEB Client ID for sign-in (client_type: 3)
      clientId = '656278119230-qep1aon07kqn1f7prjpar3urq6c8bbdr.apps.googleusercontent.com';
    } else {
      clientId = '5370505998-hakrhril4cl729qhlhg5gq2c67c9hds9.apps.googleusercontent.com';
    }
    
    await GoogleAuth.initialize({
      clientId: clientId,
      scopes: ['profile', 'email'],
      grantOfflineAccess: true,
    });
    console.log('GoogleAuth initialized successfully with client:', platform, clientId);
  } catch (error) {
    console.error('GoogleAuth initialization error:', error);
  }
})();

const rootEl = document.getElementById("root");
const loadingEl = document.getElementById("app-loading");

function hideSplash() {
  try {
    loadingEl?.remove();
  } catch {
    /* ignore */
  }
}

const ErrorFallback = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: 24,
      background: '#0a0a0a',
      color: '#e0e0e0',
      fontFamily: 'system-ui',
      textAlign: 'center',
    }}
  >
    <p style={{ marginBottom: 16, fontSize: 16, fontWeight: 600 }}>
      Something went wrong loading Straiv.
    </p>
    <button
      type="button"
      onClick={() => {
        if (Capacitor.isNativePlatform()) {
          window.location.reload();
          return;
        }
        const url = new URL(window.location.href);
        url.searchParams.set('cb', String(Date.now()));
        window.location.replace(url.toString());
      }}
      style={{
        padding: '12px 24px',
        fontSize: 16,
        background: '#00ffa3',
        color: '#000',
        border: 'none',
        borderRadius: 8,
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      Reload
    </button>
  </div>
);

function mountApp() {
  if (!rootEl) {
    console.error("[STRAIV] #root missing — cannot mount app");
    hideSplash();
    return;
  }
  try {
    createRoot(rootEl).render(
      <React.StrictMode>
        <ErrorBoundary fallback={<ErrorFallback />}>
          <BrowserRouter>
            <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
              <AuthProvider>
                <App onReady={hideSplash} />
              </AuthProvider>
            </ThemeProvider>
          </BrowserRouter>
        </ErrorBoundary>
      </React.StrictMode>
    );
    // Ensure splash clears even if a child hook fails to run (WKWebView / huge bundle edge cases)
    requestAnimationFrame(() => {
      requestAnimationFrame(hideSplash);
    });
  } catch (e) {
    console.error("[STRAIV] Failed to mount React root:", e);
    hideSplash();
    rootEl.innerHTML =
      '<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;padding:24px;background:#1a1a1a;color:#e0e0e0;font-family:system-ui;text-align:center"><p style="margin-bottom:16px">Could not start the app.</p><button type="button" onclick="location.reload()" style="padding:12px 24px;font-size:16px;background:#00ffa3;color:#000;border:none;border-radius:8px;font-weight:600">Reload</button></div>';
  }
}

// Initialise i18next BEFORE mounting React so the very first paint already
// renders in the user's chosen / device language. We don't block on it
// indefinitely - if it somehow takes more than 2s (it shouldn't, since
// translations are bundled) we mount anyway and the language switches in
// place once it's ready.
Promise.race([
  initI18n(),
  new Promise((resolve) => setTimeout(resolve, 2000)),
])
  .catch((err) => console.warn('[STRAIV] i18n init failed, mounting in English:', err))
  .finally(mountApp);
